import type { TFunction } from 'i18next';

import type { AuditChange } from '@/components/shared/audit-trail-panel';
import { formatDate } from '@/utils/format';

/**
 * The activity title says what kind of change it was.
 * This adds which person, request, device, or record it happened to.
 */

export interface AuditContextRow {
  action: string;
  entityType: string;
  summary: string;
  actorName: string | null;
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

export interface AuditContext {
  description?: string;
  facts: AuditChange[];
}

const CONTEXT_ONLY = new Set([
  'member.create',
  'member.inactive',
  'member.restore',
  'member.promote',
  'member.transfer',
  'member.deceased',
  'member.import',
  'member.export',
  'member.delete',
  'member.anonymise',
  'member.link_user',
  'member.merge',
  'member.family_link.create',
  'member.family_link.remove',
  'member_join_request.submit',
  'member_join_request.approve',
  'member_join_request.merge',
  'member_join_request.reject',
  'device.enrol',
  'device.revoke',
  'device.remove_revoked',
  'login_request.approve',
  'login_request.reject',
  'login_request.consume',
  'prayer.read',
  'pastoral.counseling.read',
  'welfare.read',
  'report.export',
  'user.account.delete',
  'totp.enable',
  'totp.disable',
  'audit.export',
  'branch.create',
  'branch.switch',
  'branch.set_as_head',
]);

export const isContextOnlyAction = (action: string): boolean =>
  CONTEXT_ONLY.has(action) || action.includes('support_ticket');

const OPENED_ACTIONS = new Set([
  'prayer.read',
  'pastoral.counseling.read',
  'welfare.read',
  'giving.identity.view',
]);

/** Who did it, written as a sentence so a bare name is not mistaken for the record. */
export const formatAuditActor = (
  action: string,
  actorName: string | null,
  t: TFunction,
): string | undefined => {
  if (!actorName) return undefined;
  if (OPENED_ACTIONS.has(action)) return t('context.openedBy', { name: actorName });
  return t('context.by', { name: actorName });
};

const metaString = (metadata: Record<string, unknown> | null, key: string): string | null => {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const metaNumber = (metadata: Record<string, unknown> | null, key: string): number | null => {
  const value = metadata?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const stringList = (metadata: Record<string, unknown> | null, key: string): string[] => {
  const value = metadata?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
};

const nameFrom = (record: Record<string, unknown> | null): string | null => {
  if (!record) return null;
  const subject = metaString(record, 'subjectName') ?? metaString(record, 'name');
  if (subject) return subject;
  const first = metaString(record, 'firstName');
  const last = metaString(record, 'lastName');
  const joined = [first, last].filter(Boolean).join(' ');
  return joined || null;
};

const subjectName = (row: AuditContextRow): string | null =>
  metaString(row.metadata, 'subjectName') ||
  nameFrom(row.afterValue) ||
  nameFrom(row.beforeValue) ||
  metaString(row.metadata, 'name') ||
  metaString(row.metadata, 'fundName') ||
  metaString(row.metadata, 'branchName') ||
  metaString(row.afterValue, 'branchName') ||
  metaString(row.metadata, 'payee') ||
  metaString(row.afterValue, 'payee') ||
  null;

const quotedName = (summary: string): string | null => {
  const match = summary.match(/"([^"]+)"/);
  const name = match?.[1]?.trim();
  if (!name || /[0-9a-f]{8}-[0-9a-f]{4}-/i.test(name)) return null;
  return name;
};

const ticketLabel = (number: number): string => String(number).padStart(4, '0');

const MACHINE_REASONS: Record<string, string> = {
  device_limit_eviction: 'context.deviceEvicted',
  account_deleted: 'context.accountDeleted',
};

const displayReason = (reason: string, t: TFunction): string | null => {
  const known = MACHINE_REASONS[reason];
  if (known) return t(known);
  if (/^[a-z0-9_]+$/.test(reason)) return null;
  return reason;
};

const translate = (t: TFunction, key: string, fallback: string): string => {
  const value = t(key, { defaultValue: fallback });
  return value && value !== key ? value : fallback;
};

const humanize = (code: string): string =>
  code
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export const buildAuditContext = (
  row: AuditContextRow,
  options: {
    t: TFunction;
    locale: string;
    fieldLabel: (field: string) => string;
    statusLabel: (code: string) => string;
  },
): AuditContext => {
  const { t, locale, fieldLabel, statusLabel } = options;
  const metadata = row.metadata;
  const name =
    subjectName(row) || (row.action.startsWith('branch.') ? quotedName(row.summary) : null);
  const email = metaString(metadata, 'email');
  const facts: AuditChange[] = [];

  const addFact = (field: string, after: string | null) => {
    if (!after) return;
    facts.push({ field, before: null, after });
  };

  const withDetail = (primary: string, detail: string) =>
    t('context.withDetail', { name: primary, detail });

  const categoryLabel = (code: string | null) =>
    code ? translate(t, `prayer:category.${code}`, humanize(code)) : null;
  const welfareTypeLabel = (code: string | null) =>
    code ? translate(t, `spending:welfare.types.${code}`, humanize(code)) : null;
  const relationshipLabel = (code: string | null) =>
    code ? translate(t, `people:members.relationship.${code}`, humanize(code)) : null;
  const memberTypeLabel = (code: string | null) =>
    code ? translate(t, `people:members.type.${code}`, humanize(code)) : null;
  const platformLabel = (code: string | null) =>
    code ? translate(t, `context.platform.${code}`, humanize(code)) : null;
  const formatLabel = (code: string | null) =>
    code ? translate(t, `context.fileFormat.${code}`, code.toUpperCase()) : null;
  const prayerStatusLabel = (code: string | null) =>
    code ? translate(t, `prayer:status.${code}`, statusLabel(code)) : null;
  const counselingStatusLabel = (code: string | null) =>
    code ? translate(t, `pastoral:counseling.status.${code}`, statusLabel(code)) : null;
  const welfareStatusLabel = (code: string | null) =>
    code ? translate(t, `spending:welfare.status.${code}`, statusLabel(code)) : null;

  const reportName = (): string | null => {
    if (metadata?.combined === true) return t('context.combinedReport');
    const explicit = metaString(metadata, 'reportName');
    if (explicit) return explicit;
    const nameKey = metaString(metadata, 'reportNameKey');
    if (!nameKey) return null;
    return translate(t, `reports:${nameKey}`, humanize(metaString(metadata, 'reportKey') ?? nameKey));
  };

  let description: string | undefined;

  if (row.action.startsWith('member_join_request.')) {
    description = name && email ? t('context.withEmail', { name, email }) : (name ?? email ?? undefined);
    const related = metaString(metadata, 'relatedName');
    if (related && related !== name) addFact(fieldLabel('relatedName'), related);
  } else if (row.action === 'prayer.read') {
    const category = categoryLabel(metaString(metadata, 'category'));
    const anonymous = metadata?.isAnonymous === true;
    if (category && anonymous) description = t('context.prayerAnonymous', { category });
    else if (category && name) description = t('context.prayerNamed', { category, name });
    else description = category ?? name ?? undefined;
    addFact(fieldLabel('status'), prayerStatusLabel(metaString(metadata, 'status')));
  } else if (row.action === 'device.remove_revoked') {
    const names = stringList(metadata, 'deviceNames');
    const count = metaNumber(metadata, 'removedCount') ?? names.length;
    description =
      names.length > 0 && names.length <= 3
        ? names.join(', ')
        : count > 0
          ? t('context.removedDevices', { count })
          : undefined;
  } else if (row.action === 'device.enrol' || row.action === 'device.revoke') {
    const device = metaString(metadata, 'deviceName');
    const owner = metaString(metadata, 'ownerName');
    const ownerIsActor = owner !== null && owner === row.actorName;
    if (device && owner && !ownerIsActor) description = t('context.deviceOf', { device, owner });
    else if (device) description = device;
    else if (owner) description = t('context.ownersDevice', { owner });
    addFact(fieldLabel('platform'), platformLabel(metaString(metadata, 'platform')));
  } else if (row.action.startsWith('login_request.')) {
    description =
      name && email && name !== email
        ? t('context.withEmail', { name, email })
        : (email ?? name ?? undefined);
  } else if (row.action.startsWith('member.family_link')) {
    const related =
      metaString(metadata, 'relatedName') || metaString(metadata, 'relatedExternalName');
    const relationship = relationshipLabel(metaString(metadata, 'relationship'));
    if (name && related && relationship) {
      description = t('context.family', { name, related, relationship });
    } else if (name && related) {
      description = withDetail(name, related);
    } else {
      description = name ?? related ?? undefined;
    }
  } else if (row.action === 'member.merge') {
    const duplicate = metaString(metadata, 'duplicateName');
    description =
      name && duplicate
        ? t('context.keptRemoved', { kept: name, duplicate })
        : (name ?? duplicate ?? undefined);
  } else if (row.action === 'member.import') {
    const succeeded = metaNumber(metadata, 'succeeded');
    const total = metaNumber(metadata, 'total');
    if (succeeded !== null && total !== null && succeeded !== total) {
      description = t('context.importedPartial', { succeeded, total });
    } else if (succeeded !== null) {
      description = t('context.imported', { count: succeeded });
    }
  } else if (row.action === 'member.export' || row.action === 'audit.export') {
    const count = metaNumber(metadata, 'rowCount');
    if (count !== null) description = t('context.rows', { count });
  } else if (row.action === 'report.export') {
    const report = reportName();
    description = report ?? undefined;
    addFact(fieldLabel('format'), formatLabel(metaString(metadata, 'format')));
    const count = metaNumber(metadata, 'rowCount');
    if (count !== null) addFact(fieldLabel('rowCount'), t('context.rows', { count }));
  } else if (row.action.includes('support_ticket') || row.entityType === 'support_ticket') {
    const number = metaNumber(metadata, 'number');
    const subject = metaString(metadata, 'subject');
    if (number !== null && subject) {
      description = t('context.ticket', { number: ticketLabel(number), subject });
    } else if (subject) {
      description = subject;
    } else if (number !== null) {
      description = t('context.ticketNumber', { number: ticketLabel(number) });
    }
  } else if (
    row.action.startsWith('contribution.') ||
    row.action === 'giving.identity.view'
  ) {
    const receipt = metaNumber(metadata, 'receiptNumber');
    const fund =
      metaString(metadata, 'fundName') ||
      metaString(row.afterValue, 'fundName') ||
      metaString(row.beforeValue, 'fundName');
    if (receipt !== null && fund) description = t('context.receiptFund', { number: receipt, fund });
    else if (receipt !== null) description = t('context.receipt', { number: receipt });
    else description = fund ?? undefined;
  } else if (row.action.startsWith('welfare.')) {
    const kind = welfareTypeLabel(metaString(metadata, 'requestType'));
    description = name && kind ? withDetail(name, kind) : (name ?? kind ?? undefined);
    if (row.action === 'welfare.read') {
      addFact(fieldLabel('status'), welfareStatusLabel(metaString(metadata, 'status')));
    }
  } else if (row.action === 'pastoral.counseling.read') {
    const topic = metaString(metadata, 'topic');
    description = name && topic ? withDetail(name, topic) : (name ?? topic ?? undefined);
    const sessionDate = metaString(metadata, 'sessionDate');
    if (sessionDate) {
      addFact(fieldLabel('sessionDate'), formatDate(sessionDate, { locale, style: 'medium' }));
    }
    addFact(fieldLabel('status'), counselingStatusLabel(metaString(metadata, 'status')));
  } else if (row.action.startsWith('pledge.')) {
    const fund = metaString(metadata, 'fundName');
    description = name && fund ? withDetail(name, fund) : (name ?? fund ?? undefined);
  } else if (row.action === 'member.transfer') {
    const destination = metaString(metadata, 'transferTo');
    description =
      name && destination
        ? t('context.transferredTo', { name, destination })
        : (name ?? destination ?? undefined);
  } else if (row.action.startsWith('member.') && name) {
    const membershipNumber = metaString(metadata, 'membershipNumber');
    description = membershipNumber ? withDetail(name, membershipNumber) : name;
    if (row.action === 'member.create') {
      addFact(fieldLabel('memberType'), memberTypeLabel(metaString(metadata, 'memberType')));
    }
  } else if (row.action === 'user.account.delete') {
    description = name && email ? t('context.withEmail', { name, email }) : (name ?? email ?? undefined);
  } else if (name) {
    description = name;
  }

  if (row.action === 'totp.enable' || row.action === 'totp.disable') {
    description = undefined;
  }

  const reason = metaString(metadata, 'reason');
  if (reason) addFact(fieldLabel('reason'), displayReason(reason, t));
  if (metadata?.evicted === true && !reason) {
    addFact(fieldLabel('reason'), t('context.deviceEvicted'));
  }

  return { description, facts };
};
